import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Gift, Plane, MapPin, TrendingUp } from 'lucide-react';

const money = (v) => `RM ${Number(v || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;
const money2 = (v) => `RM ${Number(v || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const IncentivesHub = () => {
  const [lp, setLp] = useState(null);
  const [tr, setTr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [l, t] = await Promise.all([
          api.get('/public/v1/incentives/leader-pool'),
          api.get('/public/v1/incentives/travel'),
        ]);
        setLp(l.data); setTr(t.data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full" /></div>;

  const netUnits = lp?.net_units || 0;
  const blockProgress = ((netUnits % 10) / 10) * 100;
  const nextTier = (tr?.tiers || []).find((t) => (tr?.full_time_gdv || 0) < t.min_gdv && t.tier !== 'L0') || null;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="incentives-hub-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-pink-500 to-indigo-600 flex items-center justify-center text-white"><Gift className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Incentives 2026</h1>
          <p className="text-sm text-gray-500">1 Jul – 31 Dec 2026 · Active</p>
        </div>
      </div>

      <Tabs defaultValue="lp">
        <TabsList>
          <TabsTrigger value="lp" data-testid="hub-tab-lp"><Gift className="w-4 h-4 mr-1" /> Leader Pool</TabsTrigger>
          <TabsTrigger value="travel" data-testid="hub-tab-travel"><Plane className="w-4 h-4 mr-1" /> Travel</TabsTrigger>
        </TabsList>

        <TabsContent value="lp" className="mt-4 space-y-4">
          <Card className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white">
            <CardContent className="p-6">
              <p className="text-white/80 text-sm">Net Units</p>
              <p className="text-4xl font-bold" data-testid="lp-net-units">{netUnits}</p>
              <div className="mt-4">
                <div className="flex justify-between text-xs text-white/80 mb-1">
                  <span>Progress to next 10-unit block</span><span>{(netUnits % 10).toFixed(2)} / 10</span>
                </div>
                <Progress value={blockProgress} className="h-2 bg-white/20" />
              </div>
              <div className="flex gap-4 mt-4 text-sm">
                <div><span className="text-white/70">Blocks: </span><b>{lp?.completed_blocks || 0}</b></div>
                <div><span className="text-white/70">Reward so far: </span><b>{money(lp?.provisional_reward_rm)}</b></div>
                <div><span className="text-white/70">Residual: </span><b>{lp?.residual_units || 0}</b></div>
              </div>
            </CardContent>
          </Card>
          <p className="text-xs text-amber-600">Every complete 10 units earns RM 2,000. Residual units forfeit at year-end if the window closes.</p>
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-600" /> Credit history</CardTitle></CardHeader>
            <CardContent>
              {(lp?.ledger || []).length === 0 ? (<p className="text-sm text-gray-400 py-4">No credits yet.</p>) : (
                <div className="space-y-2">
                  {lp.ledger.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm border-b last:border-b-0 pb-2">
                      <span className="text-gray-500">{r.spa_signed_at ? new Date(r.spa_signed_at).toLocaleDateString() : ''} · {r.kind}</span>
                      <span className={`font-medium ${r.units_delta >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>{r.units_delta >= 0 ? '+' : ''}{r.units_delta} units</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="travel" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card><CardContent className="p-5"><p className="text-sm text-gray-500">Total Eligible GDV</p><p className="text-2xl font-bold" data-testid="tr-total-gdv">{money(tr?.total_gdv)}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-sm text-gray-500">Full-Time Eligible GDV</p><p className="text-2xl font-bold text-sky-600" data-testid="tr-ft-gdv">{money(tr?.full_time_gdv)}</p></CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-600" /> Tier ladder</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(tr?.tiers || []).map((t) => {
                const reached = t.tier === tr?.provisional_tier;
                const gated = t.tier !== 'L0';
                const bal = gated ? (tr?.full_time_gdv || 0) : (tr?.total_gdv || 0);
                const qualifies = bal >= t.min_gdv;
                return (
                  <div key={t.tier} className={`flex items-center gap-3 p-3 rounded-lg border ${reached ? 'border-sky-400 bg-sky-50' : qualifies ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-100'}`} data-testid={`tier-${t.tier}`}>
                    <Badge className={reached ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-600'}>{t.tier}</Badge>
                    <span className="font-medium">{t.country}</span>
                    <span className="text-xs text-gray-400">{gated ? 'Full-Time' : 'Total'} GDV ≥ {money(t.min_gdv)}</span>
                    {reached && <Badge className="ml-auto bg-sky-100 text-sky-700">Your tier</Badge>}
                    {!reached && qualifies && <Badge className="ml-auto bg-emerald-100 text-emerald-700">Qualified</Badge>}
                  </div>
                );
              })}
            </CardContent>
          </Card>
          {nextTier && (
            <p className="text-xs text-gray-500">Next tier <b>{nextTier.tier} · {nextTier.country}</b> needs {money(nextTier.min_gdv - (tr?.full_time_gdv || 0))} more Full-Time GDV.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IncentivesHub;
